/**
 * SOLID Review
 * S — Strong. One concern: turn a decision's DRAFT of the next table into the changes that make it so —
 *     plus the few table values more than one decision builds (fresh lives, a stored reveal).
 * O — Good. A new table field needs a value in `EMPTY_TABLE` and nothing else here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. `settle` and the small builders the decisions share.
 * D — Strong. The table schema types, the Domain's enums and the derived values. No store, no port.
 */
import { GamePhase, INITIAL_LIVES } from '../../uml-components/primitives';
import type { IStateClientTable, IEntityClientReveal } from '../../ports/schemas/ClientTable';
import type { IPropsClientChanges } from '../../ports/schemas/ClientChanges';
import type { IEntityClientResume } from '../../ports/schemas/ClientSession';
import type { IPayloadReveal } from '../../ports/schemas/TableEvents';
import { deriveTable } from './deriveTable';
import { sameJson } from '../../../shared/sameJson';

/**
 * HOW A HANDLER BECAME A DECISION (frontend strict-ports plan, FE1, 2026-10-03). The handlers being
 * moved were sequences of store setters: `setActiveRoundId(…)`, `setPhase(…)`, `markGameActivity()`.
 * Each decision replays its handler's sequence on a DRAFT — a copy of the table, field for field, in
 * the same order — and `settle` reports what differs. A later write wins over an earlier one exactly as
 * it did in the store, and a write of the value already held changes nothing.
 *
 * Nested values (the room, the lives map) are REPLACED in a draft, never edited: the table a decision
 * receives is the caller's.
 */

/** The table as the store starts it, and as `resetAll` restores it: no identity, no room, IDLE. */
export const EMPTY_TABLE: IStateClientTable = {
  localPlayerId: null,
  room: null,
  phase: GamePhase.IDLE,
  activeRoundId: null,
  activeRollerId: null,
  activeChallengerId: null,
  lastDiceRoll: null,
  minimumClaimScore: 0,
  lastClaimedScore: null,
  claimWasBlind: false,
  diceRevealed: false,
  opponentClaim: null,
  revealResult: null,
  decisionCountdown: null,
  playerLives: {},
  suspendedPlayerIds: [],
  roomEnded: false,
  tableVersion: null,
  connectionState: 'disconnected',
  connectionWasDown: false,
  lastGameActivityAt: 0,
};

/** Identity and the activity stamp are read, never changed, by a decision (the store stamps the latter). */
type TTableField = Exclude<keyof IStateClientTable, 'localPlayerId' | 'lastGameActivityAt'>;
/** Every field a decision may change: all of them but identity. */
const FIELDS = (Object.keys(EMPTY_TABLE) as Array<keyof IStateClientTable>)
  .filter((k): k is TTableField => k !== 'localPlayerId' && k !== 'lastGameActivityAt');

function assign<K extends TTableField>(changes: IPropsClientChanges, key: K, value: IStateClientTable[K]): void {
  changes[key] = value;
}

export interface IConfigSettle {
  /** The table moved: the store stamps `lastGameActivityAt`. */
  readonly markActivity?: boolean;
  /** Back to the store's initial state first; `next` is then read against `EMPTY_TABLE`. */
  readonly reset?: boolean;
}

/**
 * The changes that turn `base` into `next`: every field that differs, the derived values of `next` when
 * anything does, the two instructions, and what the tab keeps (`persist`, below). A draft identical to
 * its base yields `{}` — or only `markActivity` — and the shell's save is a no-op.
 */
export function settle(base: IStateClientTable, next: IStateClientTable, options: IConfigSettle = {}): IPropsClientChanges {
  const from = options.reset === true ? EMPTY_TABLE : base;
  const changes: IPropsClientChanges = {};
  for (const key of FIELDS) {
    if (!sameJson(from[key], next[key])) assign(changes, key, next[key]);
  }
  if (options.reset === true) changes.reset = true;
  if (options.reset === true || Object.keys(changes).length > 0) changes.derived = deriveTable(next);
  if (options.markActivity === true) changes.markActivity = true;
  const resume = storedGameAfter(base, next);
  if (resume !== undefined) changes.persist = { resume };
  return changes;
}

/**
 * The game this tab remembers across a reload: the seat, with the code the player sees — or, for a room
 * without one, the first four characters of its id, as the persistence effect derived it. Null without
 * both a room and a player.
 */
export function resumeOf(table: IStateClientTable): IEntityClientResume | null {
  if (table.room === null || table.localPlayerId === null) return null;
  return {
    playerId: table.localPlayerId,
    roomId: table.room.roomId,
    roomCode: table.room.roomCode || table.room.roomId.slice(0, 4).toUpperCase(),
  };
}

/**
 * THE STORED GAME FOLLOWS THE SEAT (frontend strict-ports plan, FE2, 2026-10-03). The two rules
 * `useResumeSessionPersistence` applied by watching the store, applied where the seat changes instead:
 *
 *   - a seat that differs from the last one (another room, another code, another player) is stored;
 *   - a table that moves INTO idle forgets it. Only a transition does: an idle table that stays idle
 *     leaves the record alone, which is what let a refreshed player find their game on the way back.
 *
 * Undefined is "leave the record as it is". A reset is read against the table BEFORE it, so leaving a
 * game forgets it.
 */
function storedGameAfter(base: IStateClientTable, next: IStateClientTable): IEntityClientResume | null | undefined {
  const seat = resumeOf(next);
  if (seat !== null && !sameJson(resumeOf(base), seat)) return seat;
  if (next.phase === GamePhase.IDLE && base.phase !== GamePhase.IDLE) return null;
  return undefined;
}

/** `initPlayerLives(ids)`: every listed seat at the starting count, and nobody else. */
export function freshLives(playerIds: ReadonlyArray<string>): Record<string, number> {
  return Object.fromEntries(playerIds.map((id) => [id, INITIAL_LIVES]));
}

/** A reveal as the table stores it — the seven fields the screen and Play Again read, no more. */
export function revealOf(reveal: IPayloadReveal): IEntityClientReveal {
  return {
    losingPlayerId: reveal.losingPlayerId,
    wasBluff: reveal.wasBluff,
    claimedScore: reveal.claimedScore,
    trueScore: reveal.trueScore,
    die1: reveal.die1,
    die2: reveal.die2,
    newRollerId: reveal.newRollerId,
  };
}

/** The server's decider as the store held it: `setActiveChallengerId` turned '' into null. */
export function challengerOf(id: string | null | undefined): string | null {
  return id || null;
}
